/*
 * LE PORT DE NAVIGATION ENTRE APPS : ou vit la fiche qu'une reference designe,
 * quand aucune porte de l'ecran ne la montre.
 *
 * POURQUOI UN PORT, ET PAS UN LIEN. Une colonne `lien` designe une FAMILLE
 * (`contacts/organization`), jamais une app : l'app qui montre cette famille
 * est un fait de l'INSTALLATION (ce que l'org a installe, ce a quoi ce membre a
 * acces), et une app ne connait pas les autres. Le seul qui le sache est le
 * SHELL, qui liste les apps et leurs portes. Le socle decrit donc la FORME de
 * la question (« qui montre cette famille ? ») et le shell y repond ; meme
 * patron que `LiensProvider` et `ModelesProvider` : le socle decrit,
 * l'exterieur remplit.
 *
 * CE QU'IL NE FAIT PAS. Il ne charge aucune fiche (c'est l'annuaire, par
 * `LiensPort`), il ne juge aucun droit (le shell ne repond que parmi les apps
 * que ce membre PEUT ouvrir), il ne remplace pas `onOpenLinked` : une
 * reference qui trouve une porte dans SON ecran y va directement, sans passer
 * ici. Ce port n'existe que pour la reference SANS porte.
 *
 * SANS PORT, OU SANS DESTINATION : la fleche ne se rend pas, le nom reste
 * (G16, un geste qui ne mene nulle part coute plus cher qu'un geste absent).
 *
 * LE RETOUR FAIT PARTIE DU SAUT, ET C'EST POURQUOI CE PORT A DEUX SENS. Une
 * fiche ouverte par un lien dit d'ou elle vient et y ramene (regle du
 * fondateur, 09/09/2026 ; `docs/VUES-CONTRAT.md` section 5, « Sortie »), et un
 * saut ENTRE APPS ne fait pas exception : l'ecran d'arrivee demande donc ici le
 * titre de la fiche qu'on a quittee, que le shell a garde pour lui.
 */
import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
import type { CatalogueDuSuivi } from '@taki/data-model';

/** L'app et la porte qui montrent une famille, telles que le shell les connait. */
export interface DestinationDeFiche {
  /** L'id de l'app (registre de l'hote, liste installee de l'org). */
  app: string;
  /** Son libelle, celui que la fleche annonce (« Ouvrir dans Contact »). */
  appLabel: string;
  /** La porte de son ecran qui montre la famille : c'est la qu'on ouvre. */
  porte: string;
  /**
   * L'AJOUT TENU PAR LE SOCLE DE CETTE PORTE (la fiche vide, action `add` sans
   * `fn`), quand elle en declare un : c'est lui que le « Ajouter » d'un champ de
   * lien demande a l'arrivee (chantier `champ-lie-deux-gestes`). Absent : la
   * porte ne cree pas, ou elle cree par une fenetre de son app, qui ne saurait
   * pas rendre l'id cree au champ d'origine. Le bouton n'est alors pas propose.
   */
  creation?: { actionId: string };
}

export interface NavigationPort {
  /**
   * L'APP QUI MONTRE CETTE FAMILLE, parmi celles que ce membre peut ouvrir.
   *
   * La porte dont le jeu est EXACTEMENT `family` + `type` gagne ; a defaut, une
   * porte sans type local (elle montre toute la famille) convient aussi. Une
   * porte typee sur un AUTRE type ne convient jamais : elle n'ouvrirait pas la
   * fiche. `null` quand rien ne convient.
   */
  destination(family: string, type?: string): DestinationDeFiche | null;
  /**
   * L'APP `app` EST-ELLE OUVRABLE ICI, A SA PORTE `porte` ? Vrai quand ce
   * membre peut l'ouvrir (installee, visible, accessible) ET que son ecran
   * declare cette porte. C'est la question exacte d'un `auClic: { porte, app }`
   * (audit I16) : sans elle, l'ecran la devinait par `destination` sur le jeu
   * de la porte, qui repond pour N'IMPORTE QUELLE app montrant ce jeu, pas pour
   * celle que la declaration vise. Facultatif : un hote qui ne la fournit pas
   * garde ce repli.
   */
  peutOuvrir?(app: string, porte: string): boolean;
  /**
   * Y ALLER : ouvre la fiche `id` dans la porte de la destination, en push.
   *
   * `depuis` est le TITRE DE LA FICHE QU'ON QUITTE, ce que la puce de sortie
   * ecrira a l'arrivee (« Retour a Film institutionnel Belval »), ou `null`
   * quand l'ecran de depart n'a pas de titre a donner : une puce muette
   * n'existe pas, et l'arrivee retombe alors sur sa sortie ordinaire.
   */
  ouvrir(destination: DestinationDeFiche, id: string, depuis: string | null): void;
  /**
   * PASSER DANS LA PORTE `destination`, SANS FICHE : le « Ajouter » d'un champ
   * de lien, qui va y CREER. L'intention de creation est deja posee (signee de
   * l'app d'arrivee) ; le shell ne fait que changer d'app, en push, et retient
   * `depuis` pour la fiche vide qui s'ouvrira (`origineDuSaut(BROUILLON_ID)`) :
   * sa puce de sortie, son « Annuler » et le retour d'apres creation ramenent a
   * la fiche quittee.
   */
  ouvrirPorte(destination: DestinationDeFiche, depuis: string | null): void;
  /**
   * D'OU L'ON A SAUTE POUR OUVRIR `id` DANS L'APP MONTEE : le titre confie a
   * `ouvrir`, ou `null` (entree a froid, lien colle, F5, autre fiche).
   *
   * POURQUOI LE SHELL LE TIENT, ET PAS L'ECRAN D'ARRIVEE. Un saut entre apps
   * DEMONTE l'ecran de depart : sa memoire meurt avec lui, et celui qui se
   * monte de l'autre cote n'a jamais rien su de lui. Le seul qui survive aux
   * deux, c'est le shell. Memoire de page, comme l'origine d'un saut interne
   * (`OrigineDeLien` de `useScreen`) : un F5 la perd, et la puce redit
   * « Liste ». Ecrite dans l'adresse, elle ferait promettre au lien recopie un
   * retour vers une fiche que le destinataire n'a jamais ouverte.
   *
   * ELLE SE CONSOMME A LA PREMIERE LECTURE : un saut ne vaut que pour l'arrivee
   * qu'il a lui-meme ouverte. Redonnee a la meme fiche rouverte plus tard, elle
   * ferait depiler vers une etape empilee entre-temps.
   */
  origineDuSaut(id: string): string | null;
  /**
   * L'ICONE DU DOCK D'UNE APP, pour une carte qui ouvre une de ses fiches (les
   * fiches citees par Lia, `CartesDeFichesLia`, relues dans Canaux comme dans
   * la fenetre de communication). Facultatif : sans lui, l'icone module.
   */
  iconeDApp?(appId: string): ReactNode;
  /**
   * LE CATALOGUE DU SUIVI D'UNE FICHE (famille et type) : le libelle et les
   * options de ses champs, dans la declaration EFFECTIVE de l'app que
   * `destination` designe (celle de l'org, sinon celle du bundle). C'est avec
   * lui qu'une app qui montre le suivi d'une AUTRE app (Canaux) remet un ancien
   * message a clefs en mots (`messageDuSuiviEnMots`, @taki/data-model), comme
   * le centre de communication : seul le shell connait les declarations des
   * autres apps. `null` : aucune app visible ne montre cette fiche.
   * Facultatif : sans lui, le texte du message reste tel qu'il a ete ecrit.
   */
  catalogueDuSuivi?(family: string, type?: string): CatalogueDuSuivi | null;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU : un hote sans shell (une vue
 * montee seule, un test) n'a aucune autre app ou aller, et ce n'est pas une
 * faute.
 */
const NavigationContext = createContext<NavigationPort | null>(null);

/** Fournisseur pose par le SHELL, autour de l'app montee. */
export function NavigationProvider({
  value,
  children,
}: {
  value: NavigationPort | null;
  children: ReactNode;
}): JSX.Element {
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

/** Le port de navigation, ou `null` quand personne ne l'a monte. */
export function useNavigationPort(): NavigationPort | null {
  return useContext(NavigationContext);
}
